import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Star, Pencil } from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";

const BLANK_TIER = { label: "", rate: 0 };
const DEFAULT_TIERS = [
  { label: "Partner", rate: 450 },
  { label: "Senior Consultant", rate: 320 },
  { label: "Consultant", rate: 250 },
  { label: "Analyst", rate: 180 },
];

export default function RateCards() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [cards, setCards] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null); // card object when editing, else null
  const [form, setForm] = useState({ name: "", is_default: false, tiers: [...DEFAULT_TIERS] });
  const [members, setMembers] = useState([]);

  const load = async () => {
    const list = await api.rateCards();
    setCards(list);
  };

  useEffect(() => {
    (async () => {
      await load();
      // detect current user's role for admin gating
      try {
        const wsId = localStorage.getItem("fb_workspace_id");
        if (wsId) {
          const m = await api.members(wsId);
          setMembers(m);
        }
      } catch {}
    })();
  }, []);

  const myRole = members.find((m) => m.user_id === user?.id)?.role;
  const canEdit = myRole === "owner" || myRole === "admin";

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", is_default: cards.length === 0, tiers: [...DEFAULT_TIERS] });
    setOpenForm(true);
  };

  const openEdit = (card) => {
    setEditing(card);
    setForm({ name: card.name, is_default: card.is_default, tiers: card.tiers.map((t) => ({ ...t })) });
    setOpenForm(true);
  };

  const setTier = (i, k, v) => {
    const next = form.tiers.slice();
    next[i] = { ...next[i], [k]: k === "rate" ? Number(v) : v };
    setForm({ ...form, tiers: next });
  };
  const addTier = () => setForm({ ...form, tiers: [...form.tiers, { ...BLANK_TIER }] });
  const removeTier = (i) => setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    const clean = form.tiers.filter((t) => t.label.trim() && Number(t.rate) >= 0);
    if (!form.name.trim() || clean.length === 0) {
      toast({ title: "Missing information", description: "Give the card a name and at least one tier." });
      return;
    }
    const payload = { name: form.name.trim(), is_default: form.is_default, tiers: clean };
    try {
      if (editing) await api.updateRateCard(editing.id, payload);
      else await api.createRateCard(payload);
      setOpenForm(false);
      await load();
      toast({ title: editing ? "Rate card updated" : "Rate card created" });
    } catch (ex) {
      toast({ title: "Couldn't save", description: apiErrorMessage(ex, "") });
    }
  };

  const remove = async (card) => {
    if (!window.confirm(`Delete “${card.name}”? Projects using this card will keep their current rate but be unlinked.`)) return;
    await api.deleteRateCard(card.id);
    await load();
    toast({ title: "Rate card deleted" });
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-[#6a736f]">Rate cards</div>
          <h1 className="font-display text-4xl mt-1">Standard rates for your team</h1>
          <p className="text-sm text-[#3f4a44] mt-2 max-w-2xl">Save the hourly rates your firm charges by role or level. New projects can pick a card + tier instead of typing a rate each time.</p>
        </div>
        {canEdit && (
          <button onClick={openNew} className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium">
            <Plus size={16} /> New rate card
          </button>
        )}
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {cards.length === 0 ? (
          <div className="md:col-span-2 bg-white rounded-xl border border-cream p-8 text-center">
            <div className="font-display text-2xl">No rate cards yet</div>
            <p className="text-sm text-[#3f4a44] mt-2">Create one to keep your standard rates in one place — pull them straight into new projects.</p>
            {canEdit && (
              <button onClick={openNew} className="btn-coral inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-md text-sm font-medium">
                <Plus size={16} /> Create your first rate card
              </button>
            )}
          </div>
        ) : cards.map((card) => (
          <div key={card.id} className="bg-white rounded-xl border border-cream p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-lg flex items-center gap-2">
                  {card.name}
                  {card.is_default && <span className="text-[10px] uppercase tracking-wide bg-cream-2 text-coral px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Star size={10} /> Default</span>}
                </div>
                <div className="text-xs text-[#6a736f] mt-0.5">{card.tiers.length} tier{card.tiers.length !== 1 && "s"}</div>
              </div>
              {canEdit && (
                <div className="flex gap-1">
                  <button onClick={() => openEdit(card)} className="text-[#6a736f] hover:text-forest p-1"><Pencil size={14} /></button>
                  <button onClick={() => remove(card)} className="text-[#6a736f] hover:text-coral p-1"><Trash2 size={14} /></button>
                </div>
              )}
            </div>
            <div className="mt-3 divide-y divide-[#EAF3EE]">
              {card.tiers.map((t, i) => (
                <div key={i} className="flex items-center justify-between py-2 text-sm">
                  <span>{t.label}</span>
                  <span className="tabular-nums font-medium">${Number(t.rate).toLocaleString()}<span className="text-[#6a736f] font-normal">/hr</span></span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {openForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setOpenForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-xl w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">{editing ? "Edit rate card" : "New rate card"}</h2>
              <button type="button" onClick={() => setOpenForm(false)} className="text-[#6a736f]"><X size={18} /></button>
            </div>
            <label className="block text-sm">
              <span className="font-medium">Name</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Standard 2026"
                className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
            </label>
            <div>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium">Tiers</div>
                <button type="button" onClick={addTier} className="text-xs text-coral inline-flex items-center gap-1"><Plus size={12} /> Add tier</button>
              </div>
              <div className="mt-2 space-y-2">
                {form.tiers.map((t, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input value={t.label} onChange={(e) => setTier(i, "label", e.target.value)}
                      placeholder="Role / level"
                      className="flex-1 border border-cream rounded px-2 py-1.5 text-sm" />
                    <div className="flex items-center gap-1">
                      <span className="text-[#6a736f] text-sm">$</span>
                      <input type="number" min={0} step="any" value={t.rate}
                        onChange={(e) => setTier(i, "rate", e.target.value)}
                        className="w-24 border border-cream rounded px-2 py-1.5 text-sm text-right" />
                      <span className="text-[#6a736f] text-xs">/hr</span>
                    </div>
                    <button type="button" onClick={() => removeTier(i)} className="text-[#6a736f] hover:text-coral p-1"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_default}
                onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
              <span>Make this the default card for new projects</span>
            </label>
            <button type="submit" className="btn-coral w-full py-2.5 rounded-md text-sm font-medium">
              {editing ? "Save changes" : "Create rate card"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
